import {
  latLngToCell,
  cellToBoundary,
  cellToLatLng,
  getResolution,
  isValidCell,
} from 'h3-js';
import type {
  H3Index,
  LatLng,
  H3CellFeature,
  H3GridFeatureCollection,
} from '../types';
import type { Polygon } from 'geojson';

/**
 * H3Service (Frontend) - Operaciones H3 del lado del cliente
 *
 * @remarks
 * Servicio para operaciones H3 en el navegador.
 * Principalmente para conversión de H3 indexes a GeoJSON para visualización.
 *
 * @example
 * ```typescript
 * import { h3Service } from '@/core/spatial';
 *
 * const geojson = h3Service.h3ToGeoJSON(cells);
 * mapEngine.addLayer({
 *   id: 'h3-grid',
 *   type: 'fill',
 *   source: { type: 'geojson', data: geojson }
 * });
 * ```
 */
export class H3Service {
  /**
   * Convierte coordenadas geográficas a celda H3
   *
   * @param lat - Latitud
   * @param lng - Longitud
   * @param resolution - Resolución H3 (0-15)
   * @returns H3 index string
   */
  latLngToH3(lat: number, lng: number, resolution: number): H3Index {
    return latLngToCell(lat, lng, resolution);
  }

  /**
   * Convierte celda H3 a coordenadas del centro
   *
   * @param h3Index - H3 cell index
   * @returns Coordenadas del centro de la celda
   */
  h3ToLatLng(h3Index: H3Index): LatLng {
    const [lat, lng] = cellToLatLng(h3Index);
    return { lat, lng };
  }

  /**
   * Convierte celda H3 a polígono GeoJSON
   *
   * @param h3Index - H3 cell index
   * @returns Polígono GeoJSON con bordes de la celda
   */
  h3ToPolygon(h3Index: H3Index): Polygon {
    const boundary = cellToBoundary(h3Index, true); // true = GeoJSON format
    return {
      type: 'Polygon',
      coordinates: [boundary],
    };
  }

  /**
   * Convierte array de celdas H3 a GeoJSON FeatureCollection
   *
   * @param h3Indexes - Array de H3 indexes
   * @returns GeoJSON FeatureCollection listo para MapLibre GL
   *
   * @example
   * ```typescript
   * const cells = ["88a8a0651ffffff", "88a8a0653ffffff"];
   * const geojson = h3Service.h3ToGeoJSON(cells);
   *
   * // Usar en MapLibre GL
   * mapEngine.addLayer({
   *   id: 'h3-layer',
   *   type: 'fill',
   *   source: { type: 'geojson', data: geojson },
   *   paint: {
   *     'fill-color': '#2FA4B5',
   *     'fill-opacity': 0.3
   *   }
   * });
   * ```
   */
  h3ToGeoJSON(h3Indexes: H3Index[]): H3GridFeatureCollection {
    const resolution = h3Indexes.length > 0 ? getResolution(h3Indexes[0]) : 0;

    const features: H3CellFeature[] = h3Indexes.map((h3Index) => ({
      type: 'Feature',
      id: h3Index,
      properties: {
        h3Index,
        resolution,
      },
      geometry: this.h3ToPolygon(h3Index),
    }));

    return {
      type: 'FeatureCollection',
      features,
      metadata: {
        resolution,
        cellCount: h3Indexes.length,
        bounds: { minLng: 0, minLat: 0, maxLng: 0, maxLat: 0 },
      },
    };
  }

  /**
   * Valida si un string es un H3 index válido
   *
   * @param h3Index - String a validar
   * @returns true si es válido
   */
  isValid(h3Index: string): boolean {
    return isValidCell(h3Index);
  }

  /**
   * Obtiene la resolución de una celda H3
   *
   * @param h3Index - H3 cell index
   * @returns Resolución (0-15)
   */
  getResolution(h3Index: H3Index): number {
    return getResolution(h3Index);
  }
}

/**
 * Instancia singleton del servicio H3
 */
export const h3Service = new H3Service();
